import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Add, Remove } from "@mui/icons-material";
import { useFieldArray } from "react-hook-form"; 
import { EmployeeEducationAndProfessionalInfoSchema } from "../../config/forms/employeeFormConfig";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";

const EmployeeProfessionalInfoForm = ({
  textFieldStyles,
  errors,
  touchedFields = {},
  isSubmitted,
  register,
  watch,
  setCurrentSchema,
  control,
}) => {
  const [educationLevels, setEducationLevels] = useState([]);
  const { api } = ApiClient();

  const shouldShowError = useCallback((fieldName, index, subFieldName) => {
    if (index !== undefined && subFieldName) {
      const fieldError = errors[fieldName]?.[index]?.[subFieldName];
      const fieldTouched = touchedFields[fieldName]?.[index]?.[subFieldName];
      return !!fieldError && (fieldTouched || isSubmitted);
    }
    return !!errors[fieldName] && (touchedFields[fieldName] || isSubmitted);
  }, [errors, touchedFields, isSubmitted]);

  const { fields: educationFields, append: appendEducation, remove: removeEducation } = useFieldArray({ control, name: "educations" });
  const { fields: professionalFields, append: appendProfessional, remove: removeProfessional } = useFieldArray({ control, name: "professionals" });

  useEffect(() => {
    setCurrentSchema(EmployeeEducationAndProfessionalInfoSchema);
    api.get(END_POINT.EMPLOYEE.GET_ALL_EDUCATION_LEVELS).then(res => {
      if (res?.status === 200) setEducationLevels(res.data.data.map(item => ({ label: item.education_name, value: item.education_level_id })));
    });

    if (educationFields.length === 0) appendEducation({ institute_name: "", education_level_id: "", start_date: "", end_date: "" });
    if (professionalFields.length === 0) appendProfessional({ company_name: "", designation: "", ctc: "", start_date: "", end_date: "" });
  }, []);

  const educationConfig = useMemo(() => [
    { name: "institute_name", label: "Institution Name", sm: 6 },
    { name: "education_level_id", label: "Education Level", sm: 6, type: "select", options: educationLevels },
    { name: "start_date", label: "Start Date", sm: 6, type: "date" },
    { name: "end_date", label: "End Date", sm: 6, type: "date" },
  ], [educationLevels]);

  const professionalConfig = useMemo(() => [
    { name: "company_name", label: "Company Name", sm: 6 },
    { name: "designation", label: "Designation", sm: 6 },
    { name: "start_date", label: "Start Date", sm: 6, type: "date" },
    { name: "end_date", label: "End Date", sm: 6, type: "date" },
    { name: "ctc", label: "CTC (₹)", sm: 6, type: "number" },
  ], []);

  const renderArrayField = useCallback((section, field, index, config) => {
    const fieldName = `${section}.${index}.${config.name}`;
    const commonProps = {
      ...register(fieldName, { ...(config.type === "number" && { valueAsNumber: true }) }),
      error: shouldShowError(section, index, config.name),
      helperText: shouldShowError(section, index, config.name) ? errors[section]?.[index]?.[config.name]?.message : "",
      label: config.label,
      fullWidth: true,
      size: "small",
      InputProps: {
        ...textFieldStyles.input,
        ...(config.type === "number" && {
          inputProps: { min: 0, max: 100000000, step: "0.01" },
          sx: { "& input[type=number]": { MozAppearance: "textfield" }, "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 } },
        })
      },
      InputLabelProps: config.type === "date" ? { ...textFieldStyles.label, shrink: true } : textFieldStyles.label,
      FormHelperTextProps: textFieldStyles.helperText,
    };

    if (config.type === "select") {
      return (
        <TextField {...commonProps} select value={watch(fieldName) || ""} SelectProps={{ MenuProps: { PaperProps: { sx: { "& .MuiMenuItem-root:hover": { backgroundColor: "#DFA828", color: "white" } } } } }}>
          {config.options?.map((item, key) => (<MenuItem key={key} value={item.value}>{item.label}</MenuItem>))}
        </TextField>
      );
    }

    return (
      <TextField {...commonProps} type={config.type || "text"} onKeyDown={config.type === "number" ? (e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault() : undefined} />
    );
  }, [register, shouldShowError, errors, textFieldStyles, watch]);

  if (!control) return null;

  return (
    <Grid container spacing={4} sx={{ padding: { xs: 0, sm: "0 8%" } }}>
      <Grid item xs={12}><Typography variant="h6" color="#DFA828" sx={{ fontWeight: 600 }}>Education Information</Typography></Grid>
      <Grid item xs={12}>
        {educationFields.map((field, index) => (
          <Box key={field.id} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2, position: "relative", backgroundColor: "#fcfcfc", boxShadow: "0px 2px 4px rgba(0,0,0,0.05)" }}>
            <Grid container spacing={2}>
              {educationConfig.map(cfg => <Grid item xs={12} sm={cfg.sm} key={cfg.name}>{renderArrayField("educations", field, index, cfg)}</Grid>)}
            </Grid>
            <IconButton onClick={() => removeEducation(index)} type="button" sx={{ position: "absolute", top: -12, right: -12, backgroundColor: "#fff", color: "#d32f2f", boxShadow: "0px 2px 8px rgba(211, 47, 47, 0.25)", border: "1px solid #ffcdd2", width: 28, height: 28, transition: "all 0.2s ease-in-out", "&:hover": { backgroundColor: "#d32f2f", color: "#fff", transform: "scale(1.1)", boxShadow: "0px 4px 12px rgba(211, 47, 47, 0.4)", border: "1px solid #d32f2f" } }}>
              <Remove sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ))}
        <Box display="flex" justifyContent="center" mt={1}>
          <Button type="button" startIcon={<Add />} onClick={() => appendEducation({ institute_name: "", education_level_id: "", start_date: "", end_date: "" })} variant="outlined" size="small" sx={{ color: "#DFA828", borderColor: "#DFA828", "&:hover": { borderColor: "#bfa010", backgroundColor: "#fffdf5" } }}>Add Education</Button>
        </Box>
      </Grid>

      <Grid item xs={12}><Typography variant="h6" color="#DFA828" sx={{ fontWeight: 600 }}>Professional Information</Typography></Grid>
      <Grid item xs={12}>
        {professionalFields.map((field, index) => (
          <Box key={field.id} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", borderRadius: 2, position: "relative", backgroundColor: "#fcfcfc", boxShadow: "0px 2px 4px rgba(0,0,0,0.05)" }}>
            <Grid container spacing={2}>
              {professionalConfig.map(cfg => <Grid item xs={12} sm={cfg.sm} key={cfg.name}>{renderArrayField("professionals", field, index, cfg)}</Grid>)}
            </Grid>
            <IconButton onClick={() => removeProfessional(index)} type="button" sx={{ position: "absolute", top: -12, right: -12, backgroundColor: "#fff", color: "#d32f2f", boxShadow: "0px 2px 8px rgba(211, 47, 47, 0.25)", border: "1px solid #ffcdd2", width: 28, height: 28, transition: "all 0.2s ease-in-out", "&:hover": { backgroundColor: "#d32f2f", color: "#fff", transform: "scale(1.1)", boxShadow: "0px 4px 12px rgba(211, 47, 47, 0.4)", border: "1px solid #d32f2f" } }}>
              <Remove sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ))}
        <Box display="flex" justifyContent="center" mt={1}>
          <Button type="button" startIcon={<Add />} onClick={() => appendProfessional({ company_name: "", designation: "", ctc: "", start_date: "", end_date: "" })} variant="outlined" size="small" sx={{ color: "#DFA828", borderColor: "#DFA828", "&:hover": { borderColor: "#bfa010", backgroundColor: "#fffdf5" } }}>Add Experience</Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default EmployeeProfessionalInfoForm;